import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { usersAPI, companiesAPI, depotsAPI } from '../services/api';
import DataTable from '../components/DataTable';
import {PageHeader} from '../components/Ui';
import BiometricDevices from '../components/BiometricDevices';
import { useAuthStore } from '../store/authStore';

export default function Users() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Initialize formData with current user's company if available
  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cedula: '',
    address: '',
    password: '',
    user_type: 'driver',
    company_id: currentUser?.company_id || 1,
    depot_id: null,
  });

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchDepots();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      toast.error('Error fetching users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getPublic();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Error fetching companies');
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await depotsAPI.getPublic();
      setDepots(response.data);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate company_id is set
      if (!formData.company_id || formData.company_id === 0) {
        toast.error('Please select a company');
        return;
      }
      
      // Validate depot_id for operador users
      if (formData.user_type === 'operador' && !formData.depot_id) {
        toast.error('Please select a depot for operador users');
        return;
      }
      
      // Prepare payload - don't send password if empty for updates
      const payload = { ...formData };
      if (editingId && !payload.password) {
        delete payload.password;
      }
      
      // If not operador, clear depot_id
      if (formData.user_type !== 'operador') {
        payload.depot_id = null;
      }
      
      if (editingId) {
        console.log('Updating user with payload:', payload);
        await usersAPI.update(editingId, payload);
        toast.success('User updated successfully');
        setEditingId(null);
      } else {
        console.log('Creating user with payload:', payload);
        await usersAPI.create(payload);
        toast.success('User created successfully');
      }
      
      const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
      setFormData({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        cedula: '',
        address: '',
        password: '',
        user_type: 'corporativo',
        company_id: defaultCompanyId,
        depot_id: null,
      });
      setShowForm(false);
      fetchUsers();
    } catch (error) {
      let errorMsg = 'Error saving user';
      
      // Handle validation errors (422 Unprocessable Entity)
      if (error.response?.status === 422 && error.response?.data) {
        const errors = error.response.data;
        if (Array.isArray(errors)) {
          // Pydantic validation errors - extract field and message
          const fieldErrors = errors.map(err => {
            const field = err.loc?.[1] || 'field';
            const message = err.msg || 'Invalid value';
            return `${field}: ${message}`;
          });
          errorMsg = fieldErrors.join(', ');
        }
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
      console.error('Submit error:', error);
    }
  };

  const handleDelete = async (user) => {
    try {
      await usersAPI.delete(user.id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  const handlePasswordReset = async (user) => {
    const newPassword = prompt(`Enter new password for ${user.username}:`, '');
    if (newPassword === null) {
      // User cancelled
      return;
    }
    
    if (newPassword.trim() === '') {
      toast.error('Password cannot be empty');
      return;
    }
    
    try {
      await usersAPI.resetPassword(user.id, newPassword);
      toast.success(`Password reset successfully for ${user.username}`);
      fetchUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Error resetting password';
      toast.error(errorMsg);
      console.error('Password reset error:', error);
    }
  };

  const handleEdit = (user) => {
    // Ensure we have the user's actual company_id from the data
    const companyId = user.company_id || currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({ 
      ...user, 
      password: '',
      company_id: companyId
    });
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleAddClick = () => {
    const defaultCompanyId = currentUser?.company_id || (companies.length > 0 ? companies[0].id : 1);
    setFormData({
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      cedula: '',
      address: '',
      password: '',
      user_type: 'corporativo',
      company_id: defaultCompanyId,
      depot_id: null,
    });
    setEditingId(null);
    setShowForm(true);
  };

  const getCompanyName = (companyId, user) => {
    // Try to get company name from user object first
    if (user?.company?.name) {
      return user.company.name;
    }
    
    // Fallback to companies array
    if (companyId) {
      const company = companies.find(c => c.id === companyId);
      if (company?.name) {
        return company.name;
      }
    }
    
    // Default fallback
    return 'N/A';
  };

  const getDepotName = (depotId, user) => {
    // Try to get depot name from user object first
    if (user?.depot?.name) {
      return user.depot.name;
    }
    
    // Fallback to depots array
    if (depotId) {
      const depot = depots.find(d => d.id === depotId);
      if (depot?.name) {
        return depot.name;
      }
    }
    
    // Return empty string for non-operador users
    return '-';
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'username', label: 'Username', sortable: true },
    { key: 'first_name', label: 'First Name', sortable: true },
    { key: 'last_name', label: 'Last Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'user_type', label: 'Type', sortable: true },
    {
      key: 'company_id',
      label: 'Company',
      render: (value, row) => getCompanyName(row.company_id, row),
      sortable: true
    },
    {
      key: 'depot_id',
      label: 'Depot',
      render: (value, row) => row.user_type === 'operador' ? getDepotName(row.depot_id, row) : '-',
      sortable: true
    },
  ];

  return (
    <div className="container">
      <PageHeader title="Users" description="Manage accounts, access roles and registered biometric devices.">
      <button className="btn btn-primary" onClick={handleAddClick}>
        Add User
      </button>
      </PageHeader>

      {showForm && (
        <div className="modal active">
          <div className="modal-content">
            <span className="close" onClick={() => { 
              setShowForm(false); 
              setEditingId(null);
              setFormData({
                username: '',
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                cedula: '',
                address: '',
                password: '',
                user_type: 'corporativo',
                company_id: 1,
                depot_id: null,
              });
            }}>&times;</span>
            <h2>{editingId ? 'Edit User' : 'Add User'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={editingId !== null}
                  minLength="3"
                  maxLength="100"
                />
                <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                  3-100 characters
                </small>
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  minLength="7"
                  maxLength="20"
                />
                <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                  7-20 characters (digits and format symbols)
                </small>
              </div>
              <div className="form-group">
                <label>Cedula</label>
                <input
                  type="text"
                  value={formData.cedula}
                  onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                  required
                  minLength="5"
                  maxLength="50"
                />
                <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                  5-50 characters
                </small>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  minLength="5"
                  maxLength="500"
                />
                <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                  5-500 characters
                </small>
              </div>
              {!editingId && (
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingId}
                    minLength="12"
                    placeholder="Minimum 12 characters"
                  />
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Password must be at least 12 characters long
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>User Type</label>
                <select
                  value={formData.user_type}
                  onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
                >
                  <option value="driver">Driver</option>
                  <option value="corporativo">Corporativo</option>
                  <option value="verificador">Verificador</option>
                  <option value="operador">Operador</option>
                  {currentUser?.user_type === 'gods_eye' && (
                    <option value="gods_eye">Gods Eye</option>
                  )}
                </select>
              </div>
              {formData.user_type === 'operador' && (
                <div className="form-group">
                  <label>Depot *</label>
                  <select
                    value={formData.depot_id || ''}
                    onChange={(e) => setFormData({ ...formData, depot_id: e.target.value ? parseInt(e.target.value) : null })}
                    required
                  >
                    <option value="">Select a depot</option>
                    {depots
                      .filter(depot => depot.company_id === parseInt(formData.company_id))
                      .map((depot) => (
                        <option key={depot.id} value={depot.id}>
                          {depot.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Company</label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({ ...formData, company_id: parseInt(e.target.value) })}
                  disabled={currentUser?.user_type !== 'gods_eye'}
                >
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {currentUser?.user_type !== 'gods_eye' && (
                  <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                    Your company is automatically selected
                  </small>
                )}
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          data={users}
          columns={columns}
          searchableFields={['username', 'email', 'first_name', 'last_name']}
          itemsPerPage={10}
          onEdit={handleEdit}
          onPasswordReset={handlePasswordReset}
          onDelete={handleDelete}
          title="Users List"
        />
      )}
      {currentUser?.user_type === 'gods_eye' && <BiometricDevices users={users}/>}
    </div>
  );
}
